import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
  Tooltip,
  List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// === Helpers de tiempo/progreso ===
const TARGET_HOUR = 22;   // 10 PM (24h)
const TARGET_MIN = 15;    // :15
const TOTAL_HOURS = 10;   // 100% = 10 horas
const TOTAL_MINUTES = TOTAL_HOURS * 60; // 600 min

function getProgressInfo(now = new Date()) {
  const target = new Date(now);
  target.setHours(TARGET_HOUR, TARGET_MIN, 0, 0);

  let remainingMin = Math.max(0, Math.round((target - now) / 60000));
  let progress = 100 - (remainingMin / TOTAL_MINUTES) * 100;
  progress = Math.max(0, Math.min(100, progress));

  const hoursLeft = Math.floor(remainingMin / 60);
  const minutesLeft = remainingMin % 60;

  return { progress, hoursLeft, minutesLeft, targetDate: target };
}

// === Datos mock de "Últimas cargas de Ability" (puedes reemplazar luego) ===
const abilityLoadsMock = [
  { id: 1, fecha: '2025-11-05 18:35', registros: 12450, estado: 'Completada' },
  { id: 2, fecha: '2025-11-04 18:32', registros: 12210, estado: 'Completada' },
  { id: 3, fecha: '2025-11-03 18:30', registros: 12098, estado: 'Completada' },
];

// === Histórico de eventos (scroll) ===
// ─── Histórico de eventos (≥ 200 desde 10:00 a. m.) ────────────────────────────
const START_HOUR = 10;          // 10 a. m.
const TOTAL_EVENTS = 220;       // > 200
const STEP_MINUTES = 2;         // separación entre eventos

const pad2 = (n) => String(n).padStart(2, '0');

const MSG_WARNING = 'Sincronizaciones demasiado rápidas detectadas: se recomienda espaciar a ≥ 5 min.';

const ERROR_MSGS = [
  'Error de formato: columna "dob" con fecha en formato DD/MM cuando se esperaba YYYY-MM-DD.',
  'La columna "note_date" no coincide con "encounter_date" (desfase de 2 días).',
  'Inconsistencia: "gender" contiene valores no válidos (X, U) en 12 filas.',
  'Columna "note_text" supera el límite de 4000 caracteres en 5 filas.',
  'Mismatch: "patient_id" no existe en maestro al cruzar con "appointments".',
  'Error de formato: columna "phone" contiene caracteres alfabéticos.',
  'Error de tipo: "hba1c" tiene valores no numéricos en 14 filas.',
  'Inconsistencia: "medication_id" no coincide con catálogo de medicamentos.',
];

const SUCCESS_MSGS = [
  'Archivo de pacientes cargado correctamente (24,512 filas).',
  'Validación de claves primarias: sin duplicados en tabla "providers".',
  'Códigos CPT coinciden con catálogo maestro (100%).',
  'Mapeo de códigos ICD → HCC validado.',
  'Estructura JSON de notas clínicas validada (schema v3).',
  'Se normalizaron 100% de direcciones (USPS).',
  'Referencias cruzadas de pagadores verificadas.',
];

const ADV_SUCCESS = [
  'Resumen de encounters consolidado correctamente.',
  'Normalización de nombres completada.',
  'Indices reconstruidos sin errores.',
];

function buildEventLogMock() {
  let list = [];
  let h = START_HOUR;
  let m = 0;

  for (let i = 0; i < TOTAL_EVENTS; i++) {
    const ts = `${pad2(h)}:${pad2(m)}`;

    // Patrón determinista para mezclar severidades/fuentes
    const r = i % 12;
    let level = 'warning';
    let source = 'AdvanceMD';
    let msg = MSG_WARNING;
    let progress;

    if (r === 1) {
      level = 'success';
      source = 'Ability';
      msg = SUCCESS_MSGS[i % SUCCESS_MSGS.length];
    } else if (r === 2) {
      level = 'error';
      source = 'Ability';
      msg = ERROR_MSGS[i % ERROR_MSGS.length];
    } else if (r === 4) {
      level = 'error';
      source = 'Clinical Notes';
      // reusar pool de errores para CN
      msg = ERROR_MSGS[(i + 3) % ERROR_MSGS.length];
    } else if (r === 5) {
      level = 'success';
      source = 'AdvanceMD';
      msg = ADV_SUCCESS[i % ADV_SUCCESS.length];
    } else if (r === 7) {
      level = 'info';
      source = 'AdvanceMD';
      // progreso de medicamentos escalonado (10%..100%) y se repite
      progress = Math.min(100, 10 + ((i % 10) * 7)); // 10,17,24,...,73,80,87,94,100
      msg = `Medicamentos: progreso de ingesta al ${progress}%.`;
    } else if (r === 9) {
      level = 'success';
      source = 'Ability';
      msg = SUCCESS_MSGS[(i + 2) % SUCCESS_MSGS.length];
    } // el resto se queda como warning con el mensaje repetido

    list.push({
      id: `e${i + 1}`,
      ts,
      level,
      source,
      msg,
      ...(typeof progress === 'number' ? { progress } : {}),
    });

    // avanzar el reloj del log
    m += STEP_MINUTES;
    if (m >= 60) {
      m -= 60;
      h = (h + 1) % 24;
    }
  }

  // opcional: mostrar más recientes arriba
  return list.reverse();
}

const eventLogMock = buildEventLogMock();


const LevelIcon = ({ level }) => {
  if (level === 'success') return <DoneAllIcon color="success" />;
  if (level === 'warning') return <WarningAmberIcon color="warning" />;
  if (level === 'error') return <ErrorOutlineIcon color="error" />;
  return <InfoOutlinedIcon color="info" />;
};

export default function DataQualityValidationPage() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const { progress, hoursLeft, minutesLeft, targetDate } = useMemo(
    () => getProgressInfo(now),
    [now]
  );

  const formattedNow = useMemo(
    () => now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    [now]
  );

  const formattedTarget = useMemo(
    () => targetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    [targetDate]
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <Typography variant="h4" fontWeight="bold">
          Validación de Calidad de Información
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Esta página muestra el estado de las cargas recientes y el avance de la ingesta diaria.
        </Typography>

        <Divider />

        {/* Avance AdvanceMD */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <CloudUploadIcon />
                <Typography variant="h6">Avance de carga — AdvanceMD</Typography>
                <Chip
                  icon={<ScheduleIcon />}
                  label={`Objetivo hoy: ${formattedTarget}`}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <Tooltip title="Cálculo: 10% por hora hasta las 10:15 PM usando la hora local del navegador.">
                <InfoOutlinedIcon color="action" />
              </Tooltip>
            </Stack>

            <Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 12, borderRadius: 1 }} />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Progreso: {progress.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hora actual (navegador): {formattedNow}
                </Typography>
              </Stack>
            </Box>

            <Typography>
              {progress >= 100
                ? 'La carga alcanzó el 100%.'
                : `Faltan ${hoursLeft} ${hoursLeft === 1 ? 'hora' : 'horas'} y ${minutesLeft} minutos para completar el 100% (${formattedTarget}).`}
            </Typography>
          </Stack>
        </Paper>

        {/* Últimas cargas Ability */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CheckCircleIcon />
            <Typography variant="h6">Últimas cargas — Ability</Typography>
            <Chip label="Calidad OK" color="success" size="small" variant="outlined" />
          </Stack>

          <TableContainer component={Box}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha de carga</TableCell>
                  <TableCell align="right">Registros</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {abilityLoadsMock.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No hay registros para mostrar.</TableCell>
                  </TableRow>
                ) : (
                  abilityLoadsMock.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.fecha}</TableCell>
                      <TableCell align="right">{r.registros.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip label={r.estado} size="small" color={r.estado === 'Completada' ? 'success' : 'default'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Histórico de eventos */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <InfoOutlinedIcon />
            <Typography variant="h6">Histórico de eventos de carga</Typography>
            <Chip size="small" label="Tiempo real" variant="outlined" />
          </Stack>

          <Box sx={{
            maxHeight: 360,
            overflow: 'auto',
            pr: 1,
            '& .eventItem': {
              borderLeftWidth: 3,
              borderLeftStyle: 'solid',
              borderColor: 'divider',
              pl: 1.5,
              ml: 0.5
            }
          }}>
            <List dense>
              {eventLogMock.map((ev) => (
                <ListItem key={ev.id} className="eventItem" sx={{
                  borderColor:
                    ev.level === 'error' ? 'error.main' :
                    ev.level === 'warning' ? 'warning.main' :
                    ev.level === 'success' ? 'success.main' : 'info.main'
                }}>
                  <ListItemIcon>
                    <LevelIcon level={ev.level} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" sx={{ minWidth: 48 }} color="text.secondary">{ev.ts}</Typography>
                        <Typography variant="body2">
                          <strong>{ev.source}:</strong> {ev.msg}
                        </Typography>
                        {typeof ev.progress === 'number' && (
                          <Chip size="small" label={`${ev.progress}%`} />
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Typography variant="caption" color="text.secondary">
            *Incluye advertencias repetidas de sincronización para evidenciar picos de actividad.
          </Typography>
        </Paper>

        {/* Métricas / resúmenes */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Resumen de validaciones
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 269 pacientes duplicados detectados en la última carga.<br />
                • Campos requeridos completos en un 99.8%.<br />
                • Sin inconsistencias de fechas.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Próximas acciones
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Monitorear avance de AdvanceMD hasta 100%.<br />
                • Verificar nuevas cargas de Ability al finalizar el día.<br />
                • Exportar reporte de calidad semanal.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
